import {
  createDocumentExportFileName,
  DOCX_MIME_TYPE,
  normalizeDocumentExportContent,
  splitDocumentExportParagraphs,
  type DocumentExporter,
  type DocumentExportInput,
} from '../documentExport';

import {
  decodeDocumentLogo,
  millimetersToPixels,
  millimetersToTwips,
} from '../documentBranding';

const DEFAULT_FONT =
  'Times New Roman';

const DEFAULT_FONT_SIZE_HALF_POINTS =
  24;

const A4_WIDTH_TWIPS =
  11906;

const A4_HEIGHT_TWIPS =
  16838;

const DEFAULT_MARGIN_MM = {
  top: 25,
  right: 20,
  bottom: 20,
  left: 30,
};

const splitNonEmptyLines = (
  value:
    string |
    null |
    undefined
) =>
  String(value ?? '')
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .map(
      (line) =>
        line.trim()
    )
    .filter(Boolean);

const createDocxArtifact = async (
  input: DocumentExportInput
) => {
  const content =
    normalizeDocumentExportContent(
      input.content
    );

  if (!content.trim()) {
    throw new Error(
      'O documento não possui conteúdo para exportação.'
    );
  }

  const {
    AlignmentType,
    BorderStyle,
    Document,
    Footer,
    Header,
    ImageRun,
    NumberFormat,
    Packer,
    PageNumber,
    Paragraph,
    TextRun,
  } = await import('docx');

  const branding =
    input.branding;

  const paragraphTexts =
    splitDocumentExportParagraphs(
      content
    );

  const children =
    paragraphTexts.map(
      (text) => {
        if (!text) {
          return new Paragraph({
            spacing: {
              after: 120,
            },

            children: [
              new TextRun({
                text: '',
                font:
                  DEFAULT_FONT,
                size:
                  DEFAULT_FONT_SIZE_HALF_POINTS,
              }),
            ],
          });
        }

        return new Paragraph({
          alignment:
            AlignmentType.JUSTIFIED,

          spacing: {
            line: 360,
            after: 120,
          },

          children: [
            new TextRun({
              text,
              font:
                DEFAULT_FONT,
              size:
                DEFAULT_FONT_SIZE_HALF_POINTS,
            }),
          ],
        });
      }
    );

  const headerChildren: InstanceType<
    typeof Paragraph
  >[] = [];

  if (
    branding?.showLogo &&
    branding.logoDataUrl
  ) {
    const logo =
      decodeDocumentLogo(
        branding.logoDataUrl
      );

    const width =
      Math.max(
        1,
        millimetersToPixels(
          branding.logoWidthMm
        )
      );

    const height =
      Math.max(
        1,
        width *
        logo.height /
        logo.width
      );

    headerChildren.push(
      new Paragraph({
        alignment:
          AlignmentType.CENTER,

        spacing: {
          after: 80,
        },

        children: [
          new ImageRun({
            type:
              logo.type,

            data:
              logo.data,

            transformation: {
              width,
              height,
            },

            altText: {
              name:
                'Logomarca do escritório',
              title:
                'Logomarca do escritório',
              description:
                'Identidade visual institucional',
            },
          }),
        ],
      })
    );
  }

  if (
    branding?.showHeader &&
    branding.headerText
  ) {
    const headerLines =
      splitNonEmptyLines(
        branding.headerText
      );

    headerLines.forEach(
      (
        line,
        index
      ) => {
        headerChildren.push(
          new Paragraph({
            alignment:
              AlignmentType.CENTER,

            spacing: {
              after: 40,
            },

            children: [
              new TextRun({
                text:
                  line,

                font:
                  DEFAULT_FONT,

                size:
                  index === 0
                    ? 20
                    : 18,

                bold:
                  index === 0,
              }),
            ],
          })
        );
      }
    );
  }

  if (
    headerChildren.length >
    0
  ) {
    headerChildren.push(
      new Paragraph({
        border: {
          bottom: {
            style:
              BorderStyle.SINGLE,
            size: 4,
            color:
              'D1D5DB',
            space: 4,
          },
        },

        spacing: {
          after: 80,
        },
      })
    );
  }

  const footerChildren: InstanceType<
    typeof Paragraph
  >[] = [];

  if (
    (
      branding?.showFooter &&
      branding.footerText
    ) ||
    branding?.showPageNumbers
  ) {
    footerChildren.push(
      new Paragraph({
        border: {
          top: {
            style:
              BorderStyle.SINGLE,
            size: 4,
            color:
              'D1D5DB',
            space: 4,
          },
        },

        spacing: {
          after: 60,
        },
      })
    );
  }

  if (
    branding?.showFooter &&
    branding.footerText
  ) {
    for (
      const line of
      splitNonEmptyLines(
        branding.footerText
      )
    ) {
      footerChildren.push(
        new Paragraph({
          alignment:
            AlignmentType.CENTER,

          spacing: {
            after: 30,
          },

          children: [
            new TextRun({
              text:
                line,

              font:
                DEFAULT_FONT,

              size:
                16,
            }),
          ],
        })
      );
    }
  }

  if (
    branding?.showPageNumbers
  ) {
    footerChildren.push(
      new Paragraph({
        alignment:
          AlignmentType.CENTER,

        children: [
          new TextRun({
            font:
              DEFAULT_FONT,

            size:
              16,

            children: [
              'Página ',
              PageNumber.CURRENT,
              ' de ',
              PageNumber.TOTAL_PAGES,
            ],
          }),
        ],
      })
    );
  }

  const marginTop =
    millimetersToTwips(
      branding?.marginTopMm ??
      DEFAULT_MARGIN_MM.top
    );

  const marginRight =
    millimetersToTwips(
      branding?.marginRightMm ??
      DEFAULT_MARGIN_MM.right
    );

  const marginBottom =
    millimetersToTwips(
      branding?.marginBottomMm ??
      DEFAULT_MARGIN_MM.bottom
    );

  const marginLeft =
    millimetersToTwips(
      branding?.marginLeftMm ??
      DEFAULT_MARGIN_MM.left
    );

  const pageProperties: {
    size: {
      width: number;
      height: number;
    };

    margin: {
      top: number;
      right: number;
      bottom: number;
      left: number;
      header: number;
      footer: number;
    };

    pageNumbers?: {
      start: number;
      formatType:
        typeof NumberFormat.DECIMAL;
    };
  } = {
    size: {
      width:
        A4_WIDTH_TWIPS,

      height:
        A4_HEIGHT_TWIPS,
    },

    margin: {
      top:
        marginTop,

      right:
        marginRight,

      bottom:
        marginBottom,

      left:
        marginLeft,

      header:
        millimetersToTwips(
          8
        ),

      footer:
        millimetersToTwips(
          8
        ),
    },
  };

  if (
    branding?.showPageNumbers
  ) {
    pageProperties.pageNumbers = {
      start: 1,
      formatType:
        NumberFormat.DECIMAL,
    };
  }

  const document =
    new Document({
      creator:
        input.creator?.trim() ||
        'BM Legal OS',

      title:
        input.title.trim() ||
        'Documento',

      subject:
        input.subject?.trim() ||
        'Documento jurídico',

      description:
        'Documento gerado pelo BM Legal OS.',

      sections: [
        {
          properties: {
            page:
              pageProperties,
          },

          headers:
            headerChildren.length >
            0
              ? {
                  default:
                    new Header({
                      children:
                        headerChildren,
                    }),
                }
              : undefined,

          footers:
            footerChildren.length >
            0
              ? {
                  default:
                    new Footer({
                      children:
                        footerChildren,
                    }),
                }
              : undefined,

          children,
        },
      ],
    });

  const generatedBlob =
    await Packer.toBlob(
      document
    );

  const blob =
    generatedBlob.type ===
    DOCX_MIME_TYPE
      ? generatedBlob
      : new Blob(
          [generatedBlob],
          {
            type:
              DOCX_MIME_TYPE,
          }
        );

  return {
    format:
      'docx' as const,

    fileName:
      createDocumentExportFileName(
        input.title,
        'docx'
      ),

    mimeType:
      DOCX_MIME_TYPE,

    blob,
  };
};

export const docxDocumentExporter:
  DocumentExporter = {
  format: 'docx',

  export:
    createDocxArtifact,
};